
import React, { useState, useEffect } from 'react';
import SupervisorService from './SupervisorService';
import DoctorService from '../Doctor/DoctorService';
import { Plus } from 'lucide-react';

const SupervisorForm = ({ formData, handleFileChange, handleFormChange, handleSubmit, onCancel, editMode }) => {
  const [clinics, setClinics] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({
    vendor_profile_path: false,
  });

  // Fetch clinic list on mount
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        setLoadingClinics(true);
        const response = await DoctorService.getClinicList();
        if (response.msg !== 'Success') {
          throw new Error('Failed to fetch clinics');
        }
        setClinics(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setError(`Error fetching clinics: ${error.message}`);
      } finally {
        setLoadingClinics(false);
      }
    };
    fetchClinics();
  }, []);

  const handleFileUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      const response = await SupervisorService.uploadProfilePhoto(file);
      handleFileChange(field, { target: { files: [file] } }); // Store file object
      handleFormChange(field, response.privatePathUrl); // Store URL for profile photo

    } catch (error) {
      alert(`Error uploading ${field}: ${error.message}`);
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  return (
    <div className="form-view">
      <div className="form-header">
        <button className="back-btn" onClick={onCancel}>
          ← Back
        </button>
        <h2>{editMode ? 'Edit Supervisor' : 'Create New Supervisor'}</h2>
      </div>
      <div className="form-content">
        {error && <div className="error-message text-red-500 mb-4">{error}</div>}
        <div className="form-grid">
          <div className="form-item">
            <label className="mandatory">Profile Photo</label>
            <div className="relative w-32 h-32 mx-auto">
              <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {formData.vendor_profile_path ? (
                  <img
                    src={
                      typeof formData.vendor_profile_path === 'string'
                        ? formData.vendor_profile_path
                        : URL.createObjectURL(formData.vendor_profile_path)
                    }
                    alt="Profile Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gray-500">No Image</span>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-2 cursor-pointer hover:bg-blue-600 transition-colors">
                <Plus size={20} className="text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'vendor_profile_path')}
                  className="hidden"
                  disabled={uploading.vendor_profile_path}
                  required={!editMode}
                />
              </label>
            </div>
            {uploading.vendor_profile_path && <span className="text-sm text-gray-500">Uploading...</span>}
          </div>
          <div className="form-item">
            <label className="mandatory">Supervisor Name</label>
            <input
              type="text"
              value={formData.vendorName || ''}
              onChange={(e) => handleFormChange('vendorName', e.target.value)}
              placeholder="Enter supervisor name"
              required
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Email</label>
            <input
              type="email"
              value={formData.vendorEmail || ''}
              onChange={(e) => handleFormChange('vendorEmail', e.target.value)}
              placeholder="Enter supervisor email"
              required
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Phone</label>
            <input
              type="tel"
              value={formData.vendorConNumber || ''}
              onChange={(e) => handleFormChange('vendorConNumber', e.target.value)}
              placeholder="Enter 10-digit phone number"
              required
              maxLength={10}
              pattern="[0-9]{10}"
              title="Please enter a valid 10-digit phone number"
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Role</label>
            <input
              type="text"
              value={formData.role || ''}
              onChange={(e) => handleFormChange('role', e.target.value)}
              placeholder="Enter role"
              required
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Department</label>
            <input
              type="text"
              value={formData.department || ''}
              onChange={(e) => handleFormChange('department', e.target.value)}
              placeholder="Enter department"
              required
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Experience (years)</label>
            <input
              type="text"
              value={formData.experience || ''}
              onChange={(e) => handleFormChange('experience', e.target.value)}
              placeholder="Enter experience"
              required
            />
          </div>
          {!editMode &&
            <div className="form-item">
              <label className="mandatory">Password</label>
              <input
                type="text"
                value={formData.password || ''}
                onChange={(e) => handleFormChange('password', e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
          }
          <div className="form-item">
            <label className="mandatory">Clinic</label>
            <select
              value={formData.clinic_id || ''}
              onChange={(e) => handleFormChange('clinic_id', e.target.value)}
              required
              disabled={loadingClinics}
            >
              <option value="">Select a clinic</option>
              {clinics.map((clinic) => (
                <option key={clinic.clinicId} value={clinic.clinicId}>
                  {clinic.clinic}
                </option>
              ))}
            </select>
            {loadingClinics && <span className="text-sm text-gray-500">Loading clinics...</span>}
          </div>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={loadingClinics}
          >
            {editMode ? 'Update Supervisor' : 'Create Supervisor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupervisorForm;